import { useEffect, useState } from 'react';
import * as CONSTANTS from '@/components/profile/profileConstants';

const BACKEND_URL = '';

// Single in-memory cache shared across the app session so each page mount
// doesn't refetch the same dropdown options. An admin edit triggers a
// re-fetch via the `bumpProfileOptions()` helper exported below.
let cache = null;
let inflight = null;
const subscribers = new Set();

const CONSTANT_FALLBACK = {
  interests: CONSTANTS.INTERESTS_OPTIONS,
  expertise: CONSTANTS.EXPERTISE_OPTIONS,
  specialist_areas: CONSTANTS.SPECIALIST_AREAS_OPTIONS,
  qualifications: CONSTANTS.QUALIFICATIONS_OPTIONS,
  certifications: CONSTANTS.CERTIFICATIONS_OPTIONS,
  looking_for: CONSTANTS.LOOKING_FOR_OPTIONS,
  projects: CONSTANTS.PROJECTS_OPTIONS,
  sustainability_goals: CONSTANTS.GOALS_OPTIONS,
  challenges: CONSTANTS.CHALLENGES_OPTIONS,
  community_goals: CONSTANTS.COMMUNITY_GOALS_OPTIONS,
  priority_2026: CONSTANTS.PRIORITY_2026_OPTIONS,
  self_description: CONSTANTS.SELF_DESCRIPTION_OPTIONS,
  stakeholder_type: CONSTANTS.STAKEHOLDER_TYPE_OPTIONS,
};

async function fetchOptions() {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/profile-options`, { credentials: 'include' });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      // Merge with constants so any key the backend doesn't return (yet) still works.
      cache = { ...CONSTANT_FALLBACK, ...data };
    } catch {
      cache = { ...CONSTANT_FALLBACK };
    } finally {
      inflight = null;
      subscribers.forEach(fn => fn(cache));
    }
    return cache;
  })();
  return inflight;
}

export function bumpProfileOptions() {
  cache = null;
  fetchOptions();
}

/**
 * Returns the live dropdown options for the profile form.
 * Falls back to the static constants while the network call is in-flight or
 * if the API errors, so the form never renders an empty dropdown.
 */
export default function useProfileOptions() {
  const [opts, setOpts] = useState(cache || CONSTANT_FALLBACK);

  useEffect(() => {
    let cancelled = false;
    const onChange = (v) => { if (!cancelled) setOpts(v || CONSTANT_FALLBACK); };
    subscribers.add(onChange);
    if (!cache) {
      fetchOptions().then(onChange);
    } else {
      setOpts(cache);
    }
    return () => { cancelled = true; subscribers.delete(onChange); };
  }, []);

  return opts;
}
